import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LaunchDialogService } from '@spartacus/storefront';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../../core/global-message';
import { HttpResponseStatus } from '../../../../../core/global-message/models/response-status.model';
import { GlobalMessageActions } from '../../../../../core/global-message/store/actions';
import { ModalCloseReason } from '../../../../../core/modal';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { AppActions } from '../../../../../core/util/store/actions';
import { AsyncReturnService } from '../../../../../shared';
import { CartEntryReelCuttingConnector } from '../../connector/cart-entry-reel-cutting.connector';
import { CartEntryReelCuttingActionTypes } from '../actions/cart-entry-reel-cutting.action';
import { CartActions } from '../actions/index';

@Injectable()
export class CartEntryReelCuttingEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  addReelCutting$: Observable<
    CartActions.AddCartEntryReelCuttingSuccess | CartActions.AddCartEntryReelCuttingFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReelCuttingActionTypes.AddCartEntryReelCutting),
      map((action: CartActions.AddCartEntryReelCutting) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.add(payload.userId, payload.cartId, payload.entry, payload.reelCutting).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.AddCartEntryReelCuttingSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.AddCartEntryReelCuttingFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForAddReelCutting$: Observable<GlobalMessageActions.AddMessage | CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReelCuttingActionTypes.AddCartEntryReelCuttingSuccess),
      map((action: CartActions.AddCartEntryReelCuttingSuccess) => action.payload),
      concatMap((payload) => {
        this.launchDialogService.closeDialog(ModalCloseReason.REEL_CUTTING_SAVED);
        return [
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          }),
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.applyCartEntryReelCuttingSuccess_message',
              params: {
                entryNumber: +payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          }),
        ];
      })
    )
  );

  handleFailureForAddReelCutting$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReelCuttingActionTypes.AddCartEntryReelCuttingFail),
      map((action: CartActions.AddCartEntryReelCuttingFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.applyCartEntryReelCuttingFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeReelCutting$: Observable<
    CartActions.RemoveCartEntryReelCuttingSuccess | CartActions.RemoveCartEntryReelCuttingFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReelCuttingActionTypes.RemoveCartEntryReelCutting),
      map((action: CartActions.RemoveCartEntryReelCutting) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.remove(payload.userId, payload.cartId, payload.entry).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.RemoveCartEntryReelCuttingSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.RemoveCartEntryReelCuttingFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForRemoveReelCutting$: Observable<GlobalMessageActions.AddMessage | CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReelCuttingActionTypes.RemoveCartEntryReelCuttingSuccess),
      map((action: CartActions.RemoveCartEntryReelCuttingSuccess) => action.payload),
      concatMap((payload) => {
        this.launchDialogService.closeDialog(ModalCloseReason.REEL_CUTTING_REMOVED);
        return [
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          }),
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryReelCuttingSuccess_message',
              params: {
                entryNumber: +payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          }),
        ];
      })
    )
  );

  handleFailureForRemoveReelCutting$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReelCuttingActionTypes.RemoveCartEntryReelCuttingFail),
      map((action: CartActions.RemoveCartEntryReelCuttingFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryReelCuttingFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  calculatePrice$: Observable<CartActions.CalculateReelCuttingPriceSuccess | CartActions.CalculateReelCuttingPriceFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryReelCuttingActionTypes.CalculateReelCuttingPrice),
        map((action: CartActions.CalculateReelCuttingPrice) => action.payload),
        concatMap((payload) =>
          this.connector.calculatePrice(payload.userId, payload.cartId, payload.entry, payload.reelCutting).pipe(
            map((result) => {
              return new CartActions.CalculateReelCuttingPriceSuccess({
                ...payload,
                result,
              });
            }),
            catchError((error) =>
              of(
                new CartActions.CalculateReelCuttingPriceFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleFailureForCalculatePrice$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReelCuttingActionTypes.CalculateReelCuttingPriceFail),
      map((action: CartActions.CalculateReelCuttingPriceFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.BAD_REQUEST) {
            return new GlobalMessageActions.AddMessage({
              text: {
                raw: action.payload.error?.message,
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'cart.calculateCartEntryReelCuttingPriceFailure_message',
                params: {
                  entryNumber: +action.payload.entry.entryNumber + 1,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private connector: CartEntryReelCuttingConnector,
    private launchDialogService: LaunchDialogService,
    private asyncReturnService: AsyncReturnService
  ) {}
}
